import React from 'react';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Typography,
  Tooltip,
  Popper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Controller, Control } from 'react-hook-form';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

import { NamingElement, NamingConfig } from '../../../../types/packager-types';
import {
  ALL_NAMING_ELEMENT_TYPES,
  VALID_DATE_FORMATS,
  validateDateFormat,
} from '../constants';

// Styled component for wide popper
const WidePopper = styled(Popper)({
  '& .MuiAutocomplete-paper': {
    width: 'auto !important',
    minWidth: '300px !important',
  },
});

interface NamingElementRowProps {
  element: NamingElement;
  index: number;
  field: any;
  control: Control<any>;
  errors: any;
  configPath: 'processed_data_naming_config' | 'source_file_naming_config';
  watchedConfig: NamingConfig;
  metadataOptions: Array<{ value: string; label: string }>;
  documentDataOptions: Array<{ value: string; label: string }>;
  onRemove: (index: number) => void;
  isLoadingMetadata?: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
  draggableProps?: any;
  innerRef?: (element: HTMLElement | null) => void;
}

const NamingElementRow: React.FC<NamingElementRowProps> = ({
  element,
  index,
  field,
  control,
  errors,
  configPath,
  watchedConfig,
  metadataOptions,
  documentDataOptions,
  onRemove,
  isLoadingMetadata = false,
  isDragging = false,
  dragHandleProps,
  draggableProps,
  innerRef,
}) => {
  const renderValueField = (field: any, elementType: string) => {
    if (elementType === 'STRING_LITERAL') {
      return (
        <TextField
          {...field}
          value={field.value || ''}
          placeholder="Enter string literal"
          variant="outlined"
          size="small"
          sx={{
            flexGrow: 1,
            minWidth: 150,
            fontSize: 13,
            backgroundColor: 'background.paper',
            height: 32,
            minHeight: 32,
            boxSizing: 'border-box',
          }}
          InputProps={{
            sx: {
              height: 32,
              minHeight: 32,
              fontSize: 13,
              padding: 0,
              backgroundColor: 'background.paper',
              boxSizing: 'border-box',
            },
          }}
          inputProps={{
            style: {
              height: 32,
              minHeight: 32,
              fontSize: 13,
              padding: '0 8px',
              boxSizing: 'border-box',
            },
          }}
          data-cy={`${configPath}-naming-element-value-${index}`}
          error={!!errors[configPath]?.elements?.[index]?.value}
        />
      );
    } else if (elementType === 'METADATA') {
      return (
        <Autocomplete
          options={metadataOptions}
          getOptionLabel={(option: { value: string; label: string }) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={
            metadataOptions.find(
              (option: { value: string; label: string }) => option.value === field.value
            ) || null
          }
          onChange={(_, newValue) => {
            field.onChange(newValue?.value || '');
          }}
          sx={{
            flexGrow: 1,
            minWidth: 150,
            fontSize: 13,
            backgroundColor: 'background.paper',
            height: 32,
            minHeight: 32,
            boxSizing: 'border-box',
          }}
          PopperComponent={WidePopper}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              sx={{
                flexGrow: 1,
                minWidth: 150,
                fontSize: 13,
                backgroundColor: 'background.paper',
                height: 32,
                minHeight: 32,
                boxSizing: 'border-box',
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  height: 32,
                  minHeight: 32,
                  fontSize: 13,
                  padding: 0,
                  backgroundColor: 'background.paper',
                  boxSizing: 'border-box',
                },
              }}
              inputProps={{
                ...params.inputProps,
                style: {
                  height: 32,
                  minHeight: 32,
                  fontSize: 13,
                  padding: '0 8px',
                  boxSizing: 'border-box',
                },
              }}
              placeholder="Search metadata options..."
              variant="outlined"
              data-cy={`${configPath}-naming-element-value-${index}`}
              error={!!errors[configPath]?.elements?.[index]?.value}
              disabled={isLoadingMetadata}
            />
          )}
          loading={isLoadingMetadata}
          loadingText="Loading metadata options..."
          noOptionsText="No metadata options available"
          disabled={isLoadingMetadata}
        />
      );
    } else if (elementType === 'DOCUMENT_DATA') {
      return (
        <Select
          {...field}
          variant="outlined"
          size="small"
          sx={{
            flexGrow: 1,
            minWidth: 150,
            fontSize: 13,
            height: 32,
            minHeight: 32,
          }}
          data-cy={`${configPath}-naming-element-value-${index}`}
          error={!!errors[configPath]?.elements?.[index]?.value}
        >
          {documentDataOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    // fallback: always return a TextField
    return (
      <TextField
        {...field}
        value={field.value || ''}
        placeholder="Enter value"
        variant="outlined"
        size="small"
        sx={{
          flexGrow: 1,
          minWidth: 150,
          fontSize: 13,
          height: 32,
          minHeight: 32,
        }}
        data-cy={`${configPath}-naming-element-value-${index}`}
        error={!!errors[configPath]?.elements?.[index]?.value}
      />
    );
  };

  const shouldShowFormat =
    watchedConfig.elements?.[index]?.type === 'METADATA' &&
    watchedConfig.elements?.[index]?.value &&
    (watchedConfig.elements[index].value.includes('updated_at') ||
      watchedConfig.elements[index].value.includes('created_at'));

  return (
    <Box
      ref={innerRef}
      {...draggableProps}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 1,
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        opacity: 1,
        boxShadow: isDragging ? 4 : 0,
        zIndex: isDragging ? 1300 : 'auto',
      }}
    >
      {/* Main row - always horizontal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <span
          {...dragHandleProps}
          style={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            marginRight: 8,
          }}
        >
          <DragIndicatorIcon color="disabled" />
        </span>

        <Controller
          name={`${configPath}.elements.${index}.type`}
          control={control}
          rules={{ required: 'Element type is required' }}
          render={({ field }) => (
            <Select
              {...field}
              variant="outlined"
              size="small"
              sx={{
                width: 150,
                fontSize: 13,
                height: 32,
                minHeight: 32,
                backgroundColor: 'background.paper',
                boxSizing: 'border-box',
                padding: 0,
                flexGrow: 0,
                flexShrink: 0,
              }}
              data-cy={`${configPath}-naming-element-type-${index}`}
              error={!!errors[configPath]?.elements?.[index]?.type}
            >
              {ALL_NAMING_ELEMENT_TYPES.map((type: { value: string; label: string }) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />

        <Controller
          name={`${configPath}.elements.${index}.value`}
          control={control}
          rules={{ required: 'Value is required' }}
          render={({ field }) => renderValueField(field, watchedConfig.elements?.[index]?.type)}
        />

        <IconButton
          onClick={() => onRemove(index)}
          size="small"
          data-cy={`remove-${configPath}-naming-element-${index}`}
          disabled={watchedConfig.elements?.length === 1}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {/* Format field for metadata with updated_at or created_at */}
      {shouldShowFormat && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            ml: 25, // This aligns it with the second element (after drag handle + type selector + gap)
          }}
        >
          <Controller
            name={`${configPath}.elements.${index}.format`}
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true; // Empty is valid
                return (
                  validateDateFormat(value) ||
                  'Invalid date format. Use only valid patterns like %Y, %m, %d, etc.'
                );
              },
            }}
            render={({ field, fieldState }) => {
              const formatValue = field.value || '';

              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    {...field}
                    value={formatValue}
                    placeholder="Enter format (e.g., %Y-%m-%d)"
                    variant="outlined"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    sx={{
                      width: 183,
                      fontSize: 13,
                      backgroundColor: 'background.paper',
                      height: 32,
                      minHeight: 32,
                      boxSizing: 'border-box',
                      flexGrow: 0,
                      flexShrink: 0,
                    }}
                    InputProps={{
                      sx: {
                        height: 32,
                        minHeight: 32,
                        fontSize: 13,
                        padding: 0,
                        backgroundColor: 'background.paper',
                        boxSizing: 'border-box',
                      },
                    }}
                    inputProps={{
                      style: {
                        height: 32,
                        minHeight: 32,
                        fontSize: 13,
                        padding: '0 8px',
                        boxSizing: 'border-box',
                      },
                    }}
                    data-cy={`${configPath}-naming-element-format-${index}`}
                  />
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Valid Date Format Patterns:
                        </Typography>
                        {VALID_DATE_FORMATS.map((format) => (
                          <Typography key={format.value} variant="body2" sx={{ mb: 0.5 }}>
                            {format.label}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: 16,
                        color: 'action.disabled',
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                </Box>
              );
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default NamingElementRow;
